import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Extension as ExtensionIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Smartphone as SmartphoneIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useShortcutsContext } from '../context/ShortcutsContext';
import api from '../services/api';
import ThemePreview from '../components/ThemePreview';
import PWASettings from '../components/PWASettings';
import { normalizeShortcut, SHORTCUT_CATEGORIES } from '../data/shortcuts';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const { mode, setMode } = useThemeMode();
  const { enabled: shortcutsEnabled, vimMode, setEnabled: setShortcutsEnabled, toggleVimMode, resetShortcuts, shortcuts } = useShortcutsContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [useMockData, setUseMockData] = useState(false);

  // Company Settings
  const [companySettings, setCompanySettings] = useState({
    company_name: 'PaceEstate',
    logo_url: '',
    address_line1: '123 Estate Avenue',
    address_line2: 'Suite 100',
    city: 'Fort Lauderdale',
    state: 'FL',
    zip_code: '33301',
    phone: '+1 (954) 555-0100',
    email: 'info@paceestate.com',
    website: 'https://www.paceestate.com',
    business_hours: {
      monday: '9:00 AM - 5:00 PM',
      tuesday: '9:00 AM - 5:00 PM',
      wednesday: '9:00 AM - 5:00 PM',
      thursday: '9:00 AM - 5:00 PM',
      friday: '9:00 AM - 5:00 PM',
      saturday: 'Closed',
      sunday: 'Closed',
    },
  });

  // Commission Settings
  const [commissionSettings, setCommissionSettings] = useState({
    default_commission_rate: 25,
    minimum_commission: 500,
    staff_commission_percentage: 3,
    tiers: [
      { min_amount: 0, max_amount: 5000, rate: 25 },
      { min_amount: 5001, max_amount: 10000, rate: 23 },
      { min_amount: 10001, max_amount: null, rate: 20 },
    ],
  });

  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    default_county: 'Broward',
    tax_rates: {
      'Broward': 7.0,
      'Miami-Dade': 7.0,
      'Palm Beach': 7.0,
      'Hillsborough': 8.5,
      'Orange': 6.5,
      'Pinellas': 7.0,
    },
    apply_tax_on_buyer_premium: true,
    tax_exempt_items: false,
  });

  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    invoice_template_subject: 'Your PaceEstate Invoice - {{invoice_number}}',
    invoice_template_body: 'Dear {{client_name}},\n\nThank you for your business. Please find your invoice attached.\n\nBest regards,\nPaceEstate Team',
    reminder_template_subject: 'Payment Reminder - Invoice {{invoice_number}}',
    reminder_template_body: 'Dear {{client_name}},\n\nThis is a friendly reminder that your invoice is due.\n\nBest regards,\nPaceEstate Team',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: 'notifications@paceestate.com',
    smtp_password: '••••••••',
    smtp_from_email: 'noreply@paceestate.com',
    smtp_from_name: 'PaceEstate',
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    new_lead_notification: true,
    sale_created_notification: true,
    payment_received_notification: true,
    payment_overdue_notification: true,
    inventory_low_stock_notification: false,
    email_notifications: true,
    in_app_notifications: true,
    daily_digest: false,
    weekly_report: true,
  });

  // Integration Settings
  const [integrationSettings, setIntegrationSettings] = useState({
    square_enabled: true,
    square_status: 'connected',
    square_account_id: 'sq0idp-••••••••',
    square_location_id: 'L••••••••',
    calendar_sync_enabled: true,
    calendar_provider: 'google',
    google_calendar_id: 'primary',
    api_key_public: 'pk_live_••••••••',
    api_key_private: 'sk_live_••••••••',
  });

  const [showSMTPPassword, setShowSMTPPassword] = useState(false);
  const [newTier, setNewTier] = useState({ min_amount: '', max_amount: '', rate: '' });

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchSettings();
  }, [isAdmin]);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/settings');

      if (response.data.company) setCompanySettings(response.data.company);
      if (response.data.commission) setCommissionSettings(response.data.commission);
      if (response.data.tax) setTaxSettings(response.data.tax);
      if (response.data.email) setEmailSettings(response.data.email);
      if (response.data.notifications) setNotificationSettings(response.data.notifications);
      if (response.data.integrations) setIntegrationSettings(response.data.integrations);

      setUseMockData(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setUseMockData(true);
        setError('Settings API not available. Showing mock data for demonstration.');
      } else {
        setError(err.response?.data?.error || 'Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setError(null);
    setSuccess(null);
  };

  const saveCompanySettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Company settings updated successfully');
      } else {
        await api.put('/admin/settings/company', companySettings);
        setSuccess('Company settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const saveCommissionSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Commission settings updated successfully');
      } else {
        await api.put('/admin/settings/commission', commissionSettings);
        setSuccess('Commission settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save commission settings');
    } finally {
      setSaving(false);
    }
  };

  const saveTaxSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Tax settings updated successfully');
      } else {
        await api.put('/admin/settings/tax', taxSettings);
        setSuccess('Tax settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save tax settings');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Email settings updated successfully');
      } else {
        await api.put('/admin/settings/email', emailSettings);
        setSuccess('Email settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Notification settings updated successfully');
      } else {
        await api.put('/admin/settings/notifications', notificationSettings);
        setSuccess('Notification settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const saveIntegrationSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Integration settings updated successfully');
      } else {
        await api.put('/admin/settings/integrations', integrationSettings);
        setSuccess('Integration settings updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const addCommissionTier = () => {
    if (newTier.min_amount && newTier.rate) {
      setCommissionSettings({
        ...commissionSettings,
        tiers: [
          ...commissionSettings.tiers,
          {
            min_amount: parseFloat(newTier.min_amount),
            max_amount: newTier.max_amount ? parseFloat(newTier.max_amount) : null,
            rate: parseFloat(newTier.rate),
          },
        ].sort((a, b) => a.min_amount - b.min_amount),
      });
      setNewTier({ min_amount: '', max_amount: '', rate: '' });
    }
  };

  const removeCommissionTier = (index) => {
    setCommissionSettings({
      ...commissionSettings,
      tiers: commissionSettings.tiers.filter((_, i) => i !== index),
    });
  };

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Access denied. You need administrator privileges to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          System Settings
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert
          severity={useMockData ? 'info' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
            },
          }}
        >
          <Tab icon={<SettingsIcon />} label="Appearance" />
          <Tab icon={<BusinessIcon />} label="Company" />
          <Tab icon={<PercentIcon />} label="Commission" />
          <Tab icon={<ReceiptIcon />} label="Tax" />
          <Tab icon={<EmailIcon />} label="Email" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<ExtensionIcon />} label="Integrations" />
          <Tab icon={<SmartphoneIcon />} label="PWA" />
          <Tab icon={<KeyboardIcon />} label="Shortcuts" />
        </Tabs>

        {/* Appearance Settings Tab */}
        <TabPanel value={currentTab} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Theme Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Customize the appearance of your application
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      Theme Mode
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Display Mode</InputLabel>
                      <Select
                        value={mode}
                        label="Display Mode"
                        onChange={(e) => setMode(e.target.value)}
                      >
                        <MenuItem value="light">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SettingsIcon fontSize="small" />
                            Light Mode
                          </Box>
                        </MenuItem>
                        <MenuItem value="dark">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SettingsIcon fontSize="small" />
                            Dark Mode
                          </Box>
                        </MenuItem>
                        <MenuItem value="system">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SettingsIcon fontSize="small" />
                            System Preference
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {mode === 'system'
                        ? 'Theme will automatically match your system settings'
                        : mode === 'dark'
                        ? 'Dark mode reduces eye strain in low-light environments'
                        : 'Light mode provides a bright, classic appearance'}
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                      Quick Actions
                    </Typography>
                    <Stack spacing={2}>
                      <Button
                        variant={mode === 'light' ? 'contained' : 'outlined'}
                        onClick={() => setMode('light')}
                        fullWidth
                      >
                        Switch to Light Mode
                      </Button>
                      <Button
                        variant={mode === 'dark' ? 'contained' : 'outlined'}
                        onClick={() => setMode('dark')}
                        fullWidth
                      >
                        Switch to Dark Mode
                      </Button>
                      <Button
                        variant={mode === 'system' ? 'contained' : 'outlined'}
                        onClick={() => setMode('system')}
                        fullWidth
                      >
                        Use System Preference
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <ThemePreview />
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Company Settings Tab */}
        <TabPanel value={currentTab} index={1}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={companySettings.logo_url}
                  onChange={(e) => setCompanySettings({ ...companySettings, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={companySettings.address_line1}
                  onChange={(e) => setCompanySettings({ ...companySettings, address_line1: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={companySettings.address_line2}
                  onChange={(e) => setCompanySettings({ ...companySettings, address_line2: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={companySettings.city}
                  onChange={(e) => setCompanySettings({ ...companySettings, city: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={companySettings.state}
                    label="State"
                    onChange={(e) => setCompanySettings({ ...companySettings, state: e.target.value })}
                  >
                    <MenuItem value="FL">Florida</MenuItem>
                    <MenuItem value="GA">Georgia</MenuItem>
                    <MenuItem value="AL">Alabama</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={companySettings.zip_code}
                  onChange={(e) => setCompanySettings({ ...companySettings, zip_code: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={companySettings.phone}
                  onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={companySettings.email}
                  onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Website"
                  value={companySettings.website}
                  onChange={(e) => setCompanySettings({ ...companySettings, website: e.target.value })}
                  placeholder="https://www.example.com"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Business Hours
                </Typography>
              </Grid>

              {Object.entries(companySettings.business_hours).map(([day, hours]) => (
                <Grid item xs={12} md={6} key={day}>
                  <TextField
                    fullWidth
                    label={day.charAt(0).toUpperCase() + day.slice(1)}
                    value={hours}
                    onChange={(e) =>
                      setCompanySettings({
                        ...companySettings,
                        business_hours: { ...companySettings.business_hours, [day]: e.target.value },
                      })
                    }
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveCompanySettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Commission Settings Tab */}
        <TabPanel value={currentTab} index={2}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Default Commission Rates
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Default Commission Rate"
                  type="number"
                  value={commissionSettings.default_commission_rate}
                  onChange={(e) =>
                    setCommissionSettings({ ...commissionSettings, default_commission_rate: parseFloat(e.target.value) })
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Commission"
                  type="number"
                  value={commissionSettings.minimum_commission}
                  onChange={(e) =>
                    setCommissionSettings({ ...commissionSettings, minimum_commission: parseFloat(e.target.value) })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Staff Commission"
                  type="number"
                  value={commissionSettings.staff_commission_percentage}
                  onChange={(e) =>
                    setCommissionSettings({ ...commissionSettings, staff_commission_percentage: parseFloat(e.target.value) })
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Percentage paid to staff members"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Commission Tiers
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Set different commission rates based on sale amounts
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={2}>
                  {commissionSettings.tiers.map((tier, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body1">
                              ${tier.min_amount.toLocaleString()} - {tier.max_amount ? `$${tier.max_amount.toLocaleString()}` : 'No Limit'}
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {tier.rate}% Commission
                            </Typography>
                          </Box>
                          <IconButton
                            color="error"
                            onClick={() => removeCommissionTier(index)}
                            disabled={commissionSettings.tiers.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Add New Tier
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Min Amount"
                          type="number"
                          value={newTier.min_amount}
                          onChange={(e) => setNewTier({ ...newTier, min_amount: e.target.value })}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Max Amount (Optional)"
                          type="number"
                          value={newTier.max_amount}
                          onChange={(e) => setNewTier({ ...newTier, max_amount: e.target.value })}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Rate"
                          type="number"
                          value={newTier.rate}
                          onChange={(e) => setNewTier({ ...newTier, rate: e.target.value })}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={addCommissionTier}
                          sx={{ height: '56px' }}
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveCommissionSettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Tax Settings Tab */}
        <TabPanel value={currentTab} index={3}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Tax Configuration
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Default County</InputLabel>
                  <Select
                    value={taxSettings.default_county}
                    label="Default County"
                    onChange={(e) => setTaxSettings({ ...taxSettings, default_county: e.target.value })}
                  >
                    {Object.keys(taxSettings.tax_rates).map((county) => (
                      <MenuItem key={county} value={county}>
                        {county}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Florida County Tax Rates
                </Typography>
              </Grid>

              {Object.entries(taxSettings.tax_rates).map(([county, rate]) => (
                <Grid item xs={12} md={6} key={county}>
                  <TextField
                    fullWidth
                    label={`${county} County`}
                    type="number"
                    value={rate}
                    onChange={(e) =>
                      setTaxSettings({
                        ...taxSettings,
                        tax_rates: { ...taxSettings.tax_rates, [county]: parseFloat(e.target.value) },
                      })
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Tax Calculation Rules
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={taxSettings.apply_tax_on_buyer_premium}
                      onChange={(e) =>
                        setTaxSettings({ ...taxSettings, apply_tax_on_buyer_premium: e.target.checked })
                      }
                    />
                  }
                  label="Apply tax on buyer's premium"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={taxSettings.tax_exempt_items}
                      onChange={(e) => setTaxSettings({ ...taxSettings, tax_exempt_items: e.target.checked })}
                    />
                  }
                  label="Support tax-exempt items"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveTaxSettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Email Settings Tab */}
        <TabPanel value={currentTab} index={4}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Email Templates
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Use variables: {'{{client_name}}'}, {'{{invoice_number}}'}, {'{{amount}}'}, {'{{due_date}}'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Invoice Email Subject"
                  value={emailSettings.invoice_template_subject}
                  onChange={(e) => setEmailSettings({ ...emailSettings, invoice_template_subject: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Invoice Email Body"
                  value={emailSettings.invoice_template_body}
                  onChange={(e) => setEmailSettings({ ...emailSettings, invoice_template_body: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reminder Email Subject"
                  value={emailSettings.reminder_template_subject}
                  onChange={(e) => setEmailSettings({ ...emailSettings, reminder_template_subject: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Reminder Email Body"
                  value={emailSettings.reminder_template_body}
                  onChange={(e) => setEmailSettings({ ...emailSettings, reminder_template_body: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    SMTP Configuration
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => window.location.href = '/email-templates'}
                  >
                    Manage Email Templates
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  SMTP settings are managed by system administrator. Contact support to update.
                  To view and test email templates, use the "Manage Email Templates" button above.
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={emailSettings.smtp_host}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  value={emailSettings.smtp_port}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={emailSettings.smtp_username}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Password"
                  type={showSMTPPassword ? 'text' : 'password'}
                  value={emailSettings.smtp_password}
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSMTPPassword(!showSMTPPassword)}
                          edge="end"
                        >
                          {showSMTPPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Email"
                  value={emailSettings.smtp_from_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_email: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Name"
                  value={emailSettings.smtp_from_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_name: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveEmailSettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Notification Settings Tab */}
        <TabPanel value={currentTab} index={5}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Event Notifications
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.new_lead_notification}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, new_lead_notification: e.target.checked })
                      }
                    />
                  }
                  label="New Lead Created"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.sale_created_notification}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, sale_created_notification: e.target.checked })
                      }
                    />
                  }
                  label="New Sale Created"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.payment_received_notification}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, payment_received_notification: e.target.checked })
                      }
                    />
                  }
                  label="Payment Received"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.payment_overdue_notification}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, payment_overdue_notification: e.target.checked })
                      }
                    />
                  }
                  label="Payment Overdue"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.inventory_low_stock_notification}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          inventory_low_stock_notification: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Inventory Low Stock"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Notification Channels
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.email_notifications}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, email_notifications: e.target.checked })
                      }
                    />
                  }
                  label="Email Notifications"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.in_app_notifications}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, in_app_notifications: e.target.checked })
                      }
                    />
                  }
                  label="In-App Notifications"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Digest & Reports
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.daily_digest}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, daily_digest: e.target.checked })
                      }
                    />
                  }
                  label="Daily Activity Digest"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.weekly_report}
                      onChange={(e) =>
                        setNotificationSettings({ ...notificationSettings, weekly_report: e.target.checked })
                      }
                    />
                  }
                  label="Weekly Summary Report"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveNotificationSettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Integration Settings Tab */}
        <TabPanel value={currentTab} index={6}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Payment Integration
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h6">Square Payment</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Process credit card payments through Square
                        </Typography>
                      </Box>
                      <Chip
                        icon={integrationSettings.square_status === 'connected' ? <CheckCircleIcon /> : <CancelIcon />}
                        label={integrationSettings.square_status === 'connected' ? 'Connected' : 'Disconnected'}
                        color={integrationSettings.square_status === 'connected' ? 'success' : 'default'}
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Account ID"
                          value={integrationSettings.square_account_id}
                          disabled
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Location ID"
                          value={integrationSettings.square_location_id}
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Calendar Sync
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={integrationSettings.calendar_sync_enabled}
                            onChange={(e) =>
                              setIntegrationSettings({ ...integrationSettings, calendar_sync_enabled: e.target.checked })
                            }
                          />
                        }
                        label="Enable Calendar Sync"
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Calendar Provider</InputLabel>
                          <Select
                            value={integrationSettings.calendar_provider}
                            label="Calendar Provider"
                            onChange={(e) =>
                              setIntegrationSettings({ ...integrationSettings, calendar_provider: e.target.value })
                            }
                            disabled={!integrationSettings.calendar_sync_enabled}
                          >
                            <MenuItem value="google">Google Calendar</MenuItem>
                            <MenuItem value="outlook">Outlook Calendar</MenuItem>
                            <MenuItem value="apple">Apple Calendar</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Calendar ID"
                          value={integrationSettings.google_calendar_id}
                          onChange={(e) =>
                            setIntegrationSettings({ ...integrationSettings, google_calendar_id: e.target.value })
                          }
                          disabled={!integrationSettings.calendar_sync_enabled}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  API Keys
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Keep your API keys secure. Never share them publicly or commit them to version control.
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Public API Key"
                  value={integrationSettings.api_key_public}
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Private API Key"
                  value={integrationSettings.api_key_private}
                  disabled
                  type="password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveIntegrationSettings}
                    disabled={saving || loading}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* PWA Settings Tab */}
        <TabPanel value={currentTab} index={7}>
          <CardContent>
            <PWASettings />
          </CardContent>
        </TabPanel>

        {/* Keyboard Shortcuts Settings Tab */}
        <TabPanel value={currentTab} index={8}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Keyboard Shortcuts
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Configure keyboard shortcuts for faster navigation and actions
                </Typography>
              </Grid>

              {/* Enable/Disable Shortcuts */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={3}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={shortcutsEnabled}
                              onChange={(e) => setShortcutsEnabled(e.target.checked)}
                            />
                          }
                          label="Enable keyboard shortcuts"
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                          Enable or disable all keyboard shortcuts globally
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={vimMode}
                              onChange={(e) => toggleVimMode(e.target.checked)}
                              disabled={!shortcutsEnabled}
                            />
                          }
                          label="Enable Vim-style navigation"
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                          Use h/j/k/l keys for navigation in tables and lists
                        </Typography>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Quick Reference
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Press <strong>?</strong> anywhere to view all available shortcuts
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Press <strong>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'}+Shift+P</strong> to open the command palette
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Shortcuts Categories */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Available Shortcuts
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {Object.keys(shortcuts).filter(key => shortcuts[key].enabled).length} shortcuts enabled
                </Typography>
              </Grid>

              {/* Navigation Shortcuts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Navigation (g + key)
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {Object.entries(shortcuts)
                        .filter(([, shortcut]) => shortcut.category === SHORTCUT_CATEGORIES.NAVIGATION && shortcut.enabled)
                        .slice(0, 5)
                        .map(([key, shortcut]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">{shortcut.description}</Typography>
                            <Chip
                              label={normalizeShortcut(key)}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Action Shortcuts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Actions
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {Object.entries(shortcuts)
                        .filter(([, shortcut]) => shortcut.category === SHORTCUT_CATEGORIES.ACTIONS && shortcut.enabled)
                        .slice(0, 5)
                        .map(([key, shortcut]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">{shortcut.description}</Typography>
                            <Chip
                              label={normalizeShortcut(key)}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Search Shortcuts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Search
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {Object.entries(shortcuts)
                        .filter(([, shortcut]) => shortcut.category === SHORTCUT_CATEGORIES.SEARCH && shortcut.enabled)
                        .map(([key, shortcut]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">{shortcut.description}</Typography>
                            <Chip
                              label={normalizeShortcut(key)}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Selection Shortcuts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Selection (in tables)
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {Object.entries(shortcuts)
                        .filter(([, shortcut]) => shortcut.category === SHORTCUT_CATEGORIES.SELECTION && shortcut.enabled)
                        .slice(0, 5)
                        .map(([key, shortcut]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">{shortcut.description}</Typography>
                            <Chip
                              label={normalizeShortcut(key)}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Reset Button */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Reset to Defaults
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reset all keyboard shortcuts to their default configuration
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          if (window.confirm('Reset all shortcuts to defaults?')) {
                            resetShortcuts();
                            setSuccess('Shortcuts reset to defaults');
                            setTimeout(() => setSuccess(null), 3000);
                          }
                        }}
                      >
                        Reset Shortcuts
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Export/Import (Future Enhancement) */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Tip:</strong> Press <strong>?</strong> to view all shortcuts, or <strong>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'}+Shift+P</strong> to open the command palette for quick actions.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default Settings;
